"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import makeStyles from "../../theming/make-styles.js";
import mapObjectValues from "../../theming/utils/map-object-values.js";
import Portal from "../portal/index.js";
import { Notification as NotificationComponent } from "./notification.js";
const useStyles = makeStyles()((theme, _, classes) => ({
    container: {
        display: "flex",
        flexDirection: "column",
    },
    notifications: {
        "position": "fixed",
        "top": 0,
        "right": 0,
        "zIndex": 1020,
        "pointerEvents": "none",
        "&:not(:empty)": {
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%",
            padding: theme.spacing(80, "xl", "xl"),
            overflowY: "auto",
        },
        [theme.breakpoints.down("sm")]: {
            right: 0,
            left: 0,
            display: "grid",
            justifyContent: "center",
        },
        "& > div": {
            [`&:not(.${classes.stack}) + div`]: {
                marginTop: theme.spacing(12),
            },
            [`& + .${classes.stack}`]: {
                marginTop: theme.spacing("lg"),
            },
        },
    },
    stack: {
        "display": "inline-grid",
        "alignSelf": "flex-end",
        "& .line-clamp": {
            display: "box",
            fallbacks: [{ display: "-webkit-box" }],
            overflow: "hidden",
            lineClamp: 2,
            boxOrient: "vertical",
        },
        "& + div": {
            marginTop: theme.spacing("lg"),
        },
        "& > :nth-child(1)": {
            zIndex: 3,
            transform: "translateY(0) scale(1)",
            opacity: 1,
        },
        "& > :nth-child(2)": {
            zIndex: 2,
            transform: `translateY(${theme.size(12)}) scale(0.9)`,
            opacity: 0.85,
        },
        "& > :not(:nth-child(-n+3))": {
            boxShadow: "none",
        },
        "& > :not(:first-child)": {
            marginTop: 0,
            borderLeftColor: "transparent",
        },
        "& > *": {
            zIndex: 1,
            gridColumnStart: 1,
            gridRowStart: 1,
            transform: `translateY(${theme.size(24)}) scale(0.8)`,
            opacity: 0.7,
        },
    },
}));
const nonStack = new Set(["unstacked", "stuck"]);
function DefaultStackedFooter(_) {
    return null;
}
function sortStacks(a, b) {
    if (a[1].lastUpdated === b[1].lastUpdated)
        return 0;
    if (a[0] === "stuck")
        return -1;
    if (b[0] === "stuck")
        return 1;
    return (b[1].lastUpdated || 0) - (a[1].lastUpdated || 0);
}
export function NotificationContainer(_a) {
    var { className, stackedFooter: Footer = DefaultStackedFooter, stacks } = _a, props = __rest(_a, ["className", "stackedFooter", "stacks"]);
    const [isStacked, setIsStacked] = useState({});
    const { classes, cx } = useStyles();
    const notificationsRef = useRef(null);
    useEffect(() => {
        setIsStacked(mapObjectValues(stacks, ({ notifications }, key) => !nonStack.has(key) && notifications.length > 3));
    }, [stacks]);
    const handleStackClick = useCallback((key) => {
        setIsStacked(Object.assign(Object.assign({}, isStacked), { [key]: false }));
    }, [isStacked]);
    const handleClick = useCallback((e) => {
        for (const [key, { notifications }] of Object.entries(stacks)) {
            if (notificationsRef.current && !isStacked[key] && notifications.length > 3 && !notificationsRef.current.contains(e.target)) {
                setIsStacked(isStacked => (Object.assign(Object.assign({}, isStacked), { [key]: true })));
            }
        }
    }, [isStacked, stacks]);
    useEffect(() => {
        window.addEventListener("mousedown", handleClick);
        return () => {
            window.removeEventListener("mousedown", handleClick);
        };
    }, [handleClick]);
    return (_jsx(Portal, { children: _jsx("div", Object.assign({ ref: notificationsRef, className: cx(classes.notifications, className) }, props, { children: Object.entries(stacks)
                .sort(sortStacks)
                .map(([key, { notifications }], i) => notifications.length ? (_jsx("div", { className: cx(classes.container, { [classes.stack]: isStacked[key] && !nonStack.has(key) }), onClick: () => handleStackClick(key), children: notifications.map(notification => {
                    var _a;
                    return (_jsx(NotificationComponent, Object.assign({ shadow: true }, notification, { closable: (_a = notification.closable) !== null && _a !== void 0 ? _a : true, footer: isStacked[key] && !nonStack.has(key) && _jsx(Footer, { remaining: notifications.length - 1 }), onClose: e => {
                            e === null || e === void 0 ? void 0 : e.stopPropagation();
                        } }), notification.id));
                }) }, i)) : null) })) }));
}
export default NotificationContainer;
//# sourceMappingURL=notification-container.js.map